import type {
  AmmTelemetryView,
  AmmVenueRef,
  AmmVenueState,
  CandleSeries,
  Instrument,
  InstrumentProfile,
  InstrumentTelemetryView,
  PairListing,
  TradeFeed,
} from '@nutrimatic/sdk'

import { getActiveChainId } from '@/config/active'
import { normalizePairSummary, normalizePoolSummary } from '@/data/marketApi/normalizeMarketToken'
import { buildPoolsIdentitySeed } from '@/data/marketApi/poolsIdentitySeed'
import type {
  MarketCandle,
  OhlcvInterval,
  PairDetail,
  PairSummary,
  PoolSummary,
  RecentTrade,
} from '@/data/marketApi/types'

import { nutrimaticErrorMessage } from './errors'
import {
  buildPairBridgeByAddress,
  findBridgeByPairAddress,
  findBridgeForPool,
  findInstrumentByAddress,
  findInstrumentIdByAddress,
  poolAddressFromVenueId,
} from './ids'
import {
  composePoolSummaryFromNutrimatic,
  mapCandleInterval,
  mapCandlesToMarket,
  mapInstrumentToTokenProfile,
  mapListingToPairSummary,
  mapPairDetailFromNutrimatic,
  mapTradesToRecent,
} from './mapMarket'
import { getNutrimaticSdk } from './sdk'

export type NutrimaticCatalogSnapshot = {
  listings: readonly PairListing[]
  instruments: readonly Instrument[]
  venues: readonly AmmVenueRef[]
}

async function requireSdk() {
  const sdk = getNutrimaticSdk()
  if (!sdk) {
    throw new Error('Nutrimatic SDK is not available for this chain')
  }
  return sdk
}

function resolveListingPairAddress(
  listing: PairListing,
  venues: readonly AmmVenueRef[],
): string | undefined {
  const fromVenue = venues.find((v) => v.id === listing.venueId)?.poolAddress
  const fromId = poolAddressFromVenueId(listing.venueId)
  const addr = (fromVenue ?? fromId ?? '').toLowerCase()
  return addr || undefined
}

export async function fetchNutrimaticCatalog(): Promise<NutrimaticCatalogSnapshot> {
  const sdk = await requireSdk()
  const [listings, instruments, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listInstruments(),
    sdk.catalog.listVenues(),
  ])
  return { listings, instruments, venues }
}

export async function fetchNutrimaticPairs(params?: {
  sort?: 'volume24h' | 'tvl'
  limit?: number
}): Promise<{ pairs: PairSummary[] }> {
  const sdk = await requireSdk()
  const chainId = getActiveChainId()
  const [listings, instruments, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listInstruments(),
    sdk.catalog.listVenues(),
  ])
  const instrumentById = new Map(instruments.map((i) => [i.id, i]))

  const rows = await Promise.all(
    listings.map(async (listing) => {
      const pairAddress = resolveListingPairAddress(listing, venues)
      if (!pairAddress) return null
      let view: AmmTelemetryView | null = null
      try {
        view = await sdk.easy.read.getPoolView({ venue: listing.venueId })
      } catch {
        view = null
      }
      return { listing, view, pairAddress }
    }),
  )

  const sort = params?.sort ?? 'volume24h'
  const sorted = rows
    .filter((r): r is NonNullable<typeof r> => r != null)
    .sort((a, b) => {
      if (sort === 'tvl') {
        return (b.view?.tvlUsd ?? 0) - (a.view?.tvlUsd ?? 0)
      }
      return (b.view?.volume24hUsd ?? 0) - (a.view?.volume24hUsd ?? 0)
    })

  const limit = params?.limit ?? 100
  const pairs = sorted.slice(0, limit).map(({ listing, view, pairAddress }) =>
    normalizePairSummary(
      mapListingToPairSummary(
        listing,
        view,
        instrumentById.get(listing.baseInstrumentId),
        instrumentById.get(listing.quoteInstrumentId),
        pairAddress,
        chainId,
      ),
      chainId,
    ),
  )

  return { pairs }
}

export async function fetchNutrimaticPoolSummaries(): Promise<{ pools: PoolSummary[] }> {
  const sdk = await requireSdk()
  const chainId = getActiveChainId()
  const seed = buildPoolsIdentitySeed(chainId)
  const [listings, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listVenues(),
  ])
  const bridge = buildPairBridgeByAddress(listings, venues)

  const pools: PoolSummary[] = []
  for (const identity of seed) {
    const entry = findBridgeForPool(identity, bridge)
    if (!entry) continue

    let view: AmmTelemetryView | null = null
    let state: AmmVenueState | null = null
    try {
      view = await sdk.easy.read.getPoolView({ venue: entry.venueId })
    } catch {
      view = null
    }
    try {
      state = await sdk.venues.getAmmState(entry.venueId)
    } catch {
      state = null
    }
    pools.push(normalizePoolSummary(composePoolSummaryFromNutrimatic(identity, view, state), chainId))
  }

  return { pools }
}

export async function fetchNutrimaticPairDetail(pairAddress: string): Promise<PairDetail> {
  const sdk = await requireSdk()
  const chainId = getActiveChainId()
  const seed = buildPoolsIdentitySeed(chainId)
  const identity = seed.find((p) => p.pairAddress.toLowerCase() === pairAddress.toLowerCase())
  const [listings, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listVenues(),
  ])
  const bridge = buildPairBridgeByAddress(listings, venues)
  const entry = findBridgeByPairAddress(pairAddress, bridge)
  if (!entry) {
    throw new Error(`PAIR_NOT_FOUND: no Nutrimatic pair for ${pairAddress}`)
  }

  let view: AmmTelemetryView | null = null
  let state: AmmVenueState | null = null
  try {
    view = await sdk.easy.read.getPoolView({ venue: entry.venueId })
  } catch (e) {
    throw new Error(nutrimaticErrorMessage(e))
  }
  try {
    state = await sdk.venues.getAmmState(entry.venueId)
  } catch {
    state = null
  }
  return mapPairDetailFromNutrimatic(entry, identity, view, state, chainId)
}

export async function fetchNutrimaticOhlcv(
  pairAddress: string,
  params?: { interval?: OhlcvInterval; limit?: number },
): Promise<{ candles: MarketCandle[] }> {
  const sdk = await requireSdk()
  const [listings, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listVenues(),
  ])
  const bridge = buildPairBridgeByAddress(listings, venues)
  const entry = findBridgeByPairAddress(pairAddress, bridge)
  if (!entry) {
    return { candles: [] }
  }
  const series: CandleSeries = await sdk.easy.read.getChart({
    pair: entry.pairLabel,
    interval: mapCandleInterval(params?.interval ?? '1h'),
    limit: params?.limit ?? 200,
  })
  return { candles: mapCandlesToMarket(series) }
}

export async function fetchNutrimaticTrades(
  pairAddress: string,
  limit = 50,
): Promise<{ trades: RecentTrade[] }> {
  const sdk = await requireSdk()
  const [listings, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listVenues(),
  ])
  const bridge = buildPairBridgeByAddress(listings, venues)
  const entry = findBridgeByPairAddress(pairAddress, bridge)
  if (!entry) {
    return { trades: [] }
  }
  const feed: TradeFeed = await sdk.easy.read.getTradeFeed({ pair: entry.pairLabel, limit })
  return { trades: mapTradesToRecent(feed.events) }
}

export async function fetchNutrimaticTokenProfile(address: string) {
  const sdk = await requireSdk()
  const instruments = await sdk.catalog.listInstruments()
  const instrumentId = findInstrumentIdByAddress(address, instruments)
  const instrument = findInstrumentByAddress(address, instruments)
  if (!instrumentId || !instrument) {
    return null
  }

  let profile: InstrumentProfile | null = null
  let telemetry: InstrumentTelemetryView | null = null
  try {
    profile = await sdk.catalog.getInstrumentProfile(instrumentId)
  } catch {
    profile = null
  }
  try {
    telemetry = await sdk.easy.read.getTokenPrice({ instrument: instrumentId })
  } catch {
    try {
      telemetry = await sdk.telemetry.getInstrumentView(instrumentId)
    } catch {
      telemetry = null
    }
  }

  return mapInstrumentToTokenProfile(instrument, profile, telemetry, address)
}

/** Resolve pairLabel for live watch from Don't Panic pair address. */
export async function resolvePairLabelForAddress(
  pairAddress: string,
): Promise<{ pairLabel: string; venueId: string } | null> {
  const sdk = getNutrimaticSdk()
  if (!sdk) return null
  const [listings, venues] = await Promise.all([
    sdk.easy.read.listPairs(),
    sdk.catalog.listVenues(),
  ])
  const bridge = buildPairBridgeByAddress(listings, venues)
  const entry = findBridgeByPairAddress(pairAddress, bridge)
  if (!entry) return null
  return { pairLabel: entry.pairLabel, venueId: entry.venueId }
}
